import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { GlobalSearchResultDto, GlobalSearchUserInVenueDto, UserListItemDto, VenueDto } from '@ivy-monorepo/shared'
import { adminService } from '../services/admin.service'
import { useSearch } from '../contexts/SearchContext'
import Icon from './Icon'
import Avatar from './Avatar'

const DEBOUNCE_MS = 300
const SECTION_LIMIT = 5
const MIN_QUERY_LENGTH = 2

type FlatItem = { key: string; onSelect: () => void }

const fullName = (user: { firstName?: string; lastName?: string }): string =>
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'Unnamed'

const formatPresence = (iso?: string | null): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// Defined at module scope so its identity is stable across renders — an inline
// component would be a new type each render and remount its children (and thus
// reload every Avatar image) on every hover.
const Section = ({
  title,
  icon,
  total,
  children,
  onSeeAll,
}: {
  title: string
  icon: string
  total: number
  children: React.ReactNode
  onSeeAll?: () => void
}) => {
  if (total === 0) return null
  return (
    <div className="global-search-section">
      <div className="global-search-section-header">
        <span className="global-search-section-title">
          <Icon name={icon} />
          {title}
          <span className="global-search-count">{total}</span>
        </span>
        {onSeeAll && total > SECTION_LIMIT && (
          <button type="button" className="global-search-seeall" onClick={onSeeAll}>
            See all →
          </button>
        )}
      </div>
      <div className="global-search-section-items">{children}</div>
    </div>
  )
}

const emptyResult = (query: string): GlobalSearchResultDto => ({
  query,
  venues: { items: [], total: 0 },
  checkedInUsers: { items: [], total: 0 },
  visitedUsers: { items: [], total: 0 },
  users: { items: [], total: 0 },
})

const GlobalSearch = () => {
  const { searchValue, setSearchValue } = useSearch()
  const navigate = useNavigate()
  const location = useLocation()

  const [results, setResults] = useState<GlobalSearchResultDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  // When set, the next route change preserves the search term (used by "see all").
  const preserveOnNavRef = useRef(false)

  // Reset the per-page search filter on navigation, unless a "see all" asked to keep it.
  useEffect(() => {
    if (preserveOnNavRef.current) {
      preserveOnNavRef.current = false
    } else {
      setSearchValue('')
    }
    setOpen(false)
    setResults(null)
    setActiveIndex(-1)
  }, [location.pathname, setSearchValue])

  // Debounced cross-system search.
  useEffect(() => {
    const query = searchValue.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      abortRef.current?.abort()
      setResults(null)
      setLoading(false)
      return
    }

    const handle = setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setLoading(true)
      try {
        const data = await adminService.globalSearch(query, SECTION_LIMIT, controller.signal)
        setResults(data)
        setOpen(true)
        setActiveIndex(-1)
      } catch (err: any) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          setResults(emptyResult(query))
        }
      } finally {
        if (abortRef.current === controller) {
          setLoading(false)
        }
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(handle)
  }, [searchValue])

  // Close on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const go = useCallback(
    (path: string) => {
      preserveOnNavRef.current = false
      setOpen(false)
      navigate(path)
    },
    [navigate]
  )

  const seeAll = useCallback(
    (path: string) => {
      // Keep the term so the destination list page filters by it.
      preserveOnNavRef.current = true
      setOpen(false)
      navigate(path)
    },
    [navigate]
  )

  // Flattened, keyboard-navigable list of all visible result rows.
  const flatItems = useMemo<FlatItem[]>(() => {
    if (!results) return []
    const items: FlatItem[] = []
    results.venues.items.forEach(v => items.push({ key: `v-${v.publicId}`, onSelect: () => go(`/admin/venues/${v.publicId}`) }))
    results.checkedInUsers.items.forEach(u => items.push({ key: `c-${u.publicId}`, onSelect: () => go(`/admin/users/${u.publicId}`) }))
    results.visitedUsers.items.forEach(u => items.push({ key: `h-${u.publicId}`, onSelect: () => go(`/admin/users/${u.publicId}`) }))
    results.users.items.forEach(u => items.push({ key: `u-${u.publicId}`, onSelect: () => go(`/admin/users/${u.publicId}`) }))
    return items
  }, [results, go])

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && results) {
      setOpen(true)
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, flatItems.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && flatItems[activeIndex]) {
        flatItems[activeIndex].onSelect()
      }
    }
  }

  const hasAnyResults =
    !!results &&
    results.venues.total + results.checkedInUsers.total + results.visitedUsers.total + results.users.total > 0

  const query = searchValue.trim()
  const showPanel = open && query.length >= MIN_QUERY_LENGTH

  // Maps a flat keyboard index onto the rendered rows.
  let renderIndex = -1
  const rowActiveClass = (active: boolean) => `global-search-row${active ? ' active' : ''}`

  const renderVenue = (venue: VenueDto) => {
    renderIndex += 1
    const idx = renderIndex
    return (
      <button
        type="button"
        key={`v-${venue.publicId}`}
        className={rowActiveClass(idx === activeIndex)}
        onMouseEnter={() => setActiveIndex(idx)}
        onClick={() => go(`/admin/venues/${venue.publicId}`)}
      >
        <Avatar className="is-venue global-search-thumb" url={venue.logoUrl} firstName={venue.name} lastName={venue.name} />
        <span className="global-search-text">
          <span className="global-search-title">{venue.name}</span>
          {venue.address && <span className="global-search-subtitle">{venue.address}</span>}
        </span>
      </button>
    )
  }

  const renderUserInVenue = (user: GlobalSearchUserInVenueDto, presenceLabel: string) => {
    renderIndex += 1
    const idx = renderIndex
    return (
      <button
        type="button"
        key={`${presenceLabel}-${user.publicId}`}
        className={rowActiveClass(idx === activeIndex)}
        onMouseEnter={() => setActiveIndex(idx)}
        onClick={() => go(`/admin/users/${user.publicId}`)}
      >
        <Avatar className="global-search-thumb" url={user.imageUrl} firstName={user.firstName} lastName={user.lastName} />
        <span className="global-search-text">
          <span className="global-search-title">{fullName(user)}</span>
          <span className="global-search-subtitle">
            {user.venueName ?? 'Unknown venue'}
            {formatPresence(user.presenceAt) && ` · ${formatPresence(user.presenceAt)}`}
          </span>
        </span>
        <span className={`global-search-badge ${presenceLabel === 'c' ? 'live' : 'history'}`}>
          {presenceLabel === 'c' ? 'Now' : 'Past'}
        </span>
      </button>
    )
  }

  const renderUser = (user: UserListItemDto) => {
    renderIndex += 1
    const idx = renderIndex
    return (
      <button
        type="button"
        key={`u-${user.publicId}`}
        className={rowActiveClass(idx === activeIndex)}
        onMouseEnter={() => setActiveIndex(idx)}
        onClick={() => go(`/admin/users/${user.publicId}`)}
      >
        <Avatar className="global-search-thumb" url={user.imageUrl} firstName={user.firstName} lastName={user.lastName} />
        <span className="global-search-text">
          <span className="global-search-title">{fullName(user)}</span>
          {user.telephone && <span className="global-search-subtitle">{user.telephone}</span>}
        </span>
      </button>
    )
  }

  return (
    <div className="global-search" ref={containerRef}>
      <input
        type="text"
        aria-label="Search"
        placeholder="Search venues, users, check-ins…"
        value={searchValue}
        onChange={e => setSearchValue(e.target.value)}
        onFocus={() => results && setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {showPanel && (
        <div className="global-search-panel">
          {loading && !results && <div className="global-search-status">Searching…</div>}

          {results && (
            <>
              <Section title="Venues" icon="Shop" total={results.venues.total} onSeeAll={() => seeAll('/admin/venues')}>
                {results.venues.items.map(renderVenue)}
              </Section>

              <Section
                title="Checked-in users"
                icon="FireFlameCurved"
                total={results.checkedInUsers.total}
                onSeeAll={() => seeAll('/admin/checkedin-users')}
              >
                {results.checkedInUsers.items.map(u => renderUserInVenue(u, 'c'))}
              </Section>

              <Section
                title="Visit history"
                icon="DoorOpen"
                total={results.visitedUsers.total}
                onSeeAll={() => seeAll('/admin/visits')}
              >
                {results.visitedUsers.items.map(u => renderUserInVenue(u, 'h'))}
              </Section>

              <Section title="Users" icon="Users" total={results.users.total} onSeeAll={() => seeAll('/admin/users')}>
                {results.users.items.map(renderUser)}
              </Section>

              {!hasAnyResults && !loading && (
                <div className="global-search-status">No results for “{query}”.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default GlobalSearch
